import { useState } from 'react';
import '../../style.scss';
import { IOrganization } from '../../../../../models/partnerportalen/organization.model';
import ListItemDropDownMenu from '../../../../PaEgnaBen/Shared/ListItemDropDownMenu';
import { MenuItemType } from '../../../../PaEgnaBen/Shared/ListItemDropDownMenu/menu-item-type.enum';
import FocusTrap from '../../../../Shared/FocusTrap';
import { TranslationService } from '../../../../../services/translation.service';
import { AnalyticsService } from '../../../../../services/analytics-service';
import { Status } from '../../../../../hooks/fetch.hook';
import Alert from '../../../../Shared/Alert';
import AntiforgeryService from '../../../../../services/anti-forgery.service';
import StralkastarenAdministratorHelper from '../../helpers/stralkastaren-administrator-helper';
import { Navigate } from 'react-router-dom';

function Organizations({ organizations, onDeleted }: { organizations: IOrganization[]; onDeleted: (organization: IOrganization) => void }) {
  // === STATE ===
  const [deleteOrganizationStatus, setDeleteOrganizationStatus] = useState<{ status: Status; statusCode: number }>({ status: Status.notperformed, statusCode: 0 });
  const [selectedOrganization, setSelectedOrganization] = useState<IOrganization | undefined>(undefined);
  const [modalTriggerElement, setModalTriggerElement] = useState<string>('');
  const [organizationFilter, setOrganizationFilter] = useState('');
  const [exportStatus, setExportStatus] = useState<{ status: Status; statusCode: number }>({ status: Status.notperformed, statusCode: 0 });

  const filteredOrganizations = organizations.filter((org) => {
    const searchTerm = organizationFilter.trim().toLowerCase();
    return (
      org.name.toLowerCase().includes(searchTerm) ||
      org.address.toLowerCase().includes(searchTerm) ||
      org.postalCode.toLowerCase().includes(searchTerm) ||
      org.city.toLowerCase().includes(searchTerm) ||
      org.email.toLowerCase().includes(searchTerm)
    );
  });

  async function handleConfirmDeleteClick() {
    if (!selectedOrganization) return;

    try {
      setDeleteOrganizationStatus({ status: Status.notperformed, statusCode: 0 });
      const response = await fetch(`/api/stralkastaren/administrator/organizations/${selectedOrganization.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
      });

      setDeleteOrganizationStatus({ status: response.ok ? Status.success : Status.failed, statusCode: response.status });
      if (response.ok) {
        AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, 'Organisation borttagen', '');
        onDeleted(selectedOrganization);
      } else {
        AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, 'Organisation borttagen - misslyckat', '');
      }
      setSelectedOrganization(undefined);
    } catch (error: unknown) {
      setDeleteOrganizationStatus({ status: Status.failed, statusCode: (error as { status?: number }).status || 0 });
    }
    setModalTriggerElement('');
  }

  function handleOnClose() {
    setSelectedOrganization(undefined);
    if (modalTriggerElement) {
      document.getElementById(modalTriggerElement)?.focus();
    }
  }

  async function handleOnExportClick() {
    try {
      setExportStatus({ status: Status.loading, statusCode: 0 });

      const response = await fetch(`/api/stralkastaren/administrator/organizations/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
      });

      if (response.ok) {
        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = StralkastarenAdministratorHelper.getFileNameFromContentDispositionHeader(response.headers, 'stralkastaren-foretag.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setExportStatus({ status: Status.success, statusCode: response.status });
      } else {
        setExportStatus({ status: Status.failed, statusCode: response.status });
      }
    } catch (error: unknown) {
      setExportStatus({ status: Status.failed, statusCode: (error as { status?: number }).status || 0 });
    }
  }

  function renderDeleteModalContent() {
    return (
      <div className="modal-content">
        <div className="modal-header">
          <h2 id="sign-modal-title" className="modal-title fs-3">
            Ta bort organisation
          </h2>
          <button type="button" className="btn-close" aria-label={TranslationService.get('common.close')} onClick={handleOnClose}></button>
        </div>
        <div className="modal-body pt-0">
          <div className="mb-3">
            Vill du ta bort <strong>{selectedOrganization?.name}</strong> (organisationsnummer: <strong>{selectedOrganization?.organizationNumber}</strong>)?
          </div>
          <div className="d-grid d-md-flex gap-3">
            <button type="button" className="btn btn-secondary flex-grow-1 flex-basis-0 order-2 order-md-1" onClick={handleOnClose}>
              Avbryt
            </button>
            <button type="button" className="btn btn-primary flex-grow-1 flex-basis-0 order-1 order-md-2" onClick={handleConfirmDeleteClick}>
              Ta bort
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (deleteOrganizationStatus.status === Status.failed || exportStatus.status === Status.failed) {
    if (deleteOrganizationStatus.statusCode === 401 || exportStatus.statusCode === 401) {
      return <Navigate to="/session-utgangen" state={{ from: window.location.pathname }} />;
    }
    if (deleteOrganizationStatus.statusCode === 403 || exportStatus.statusCode === 403) {
      return <Navigate to="/atkomst-saknas" />;
    }
  }

  return (
    <>
      {selectedOrganization && (
        <FocusTrap isVisible={!!selectedOrganization} onEscape={handleOnClose}>
          <div className="modal d-block fade show" aria-labelledby="sign-modal-title" aria-modal="true" role="dialog">
            <div className="modal-dialog modal-dialog-centered">{renderDeleteModalContent()}</div>
          </div>
        </FocusTrap>
      )}
      {selectedOrganization && <div className="modal-backdrop fade show"></div>}

      {organizations && (
        <div className="organizations">
          <div className="mb-2">
            <button type="button" className="btn btn-primary" onClick={() => handleOnExportClick()} disabled={exportStatus.status === Status.loading}>
              Exportera företag (.csv)
            </button>
            {exportStatus.status === Status.failed && (
              <Alert show={true} heading="Kunde inte exportera företag, försök igen." closable={false} className="mt-2" />
            )}
          </div>
          <div className="mb-3 px-1">
            <label htmlFor="organization-filter" className="form-label">
              Fritextsök
            </label>
            <input
              type="text"
              id="organization-filter"
              className="form-control"
              placeholder="T.ex. företagsnamn, leveransadress, e-post ..."
              value={organizationFilter}
              onChange={(e) => setOrganizationFilter(e.target.value)}
              aria-label="Filtrera på e-post"
              autoComplete="off"
            />
          </div>

          <table className="table">
            <thead>
              <tr>
                <th scope="col" className="name-column">
                  Företagsnamn
                </th>
                <th scope="col" className="address-column">
                  Leveransadress
                </th>
                <th scope="col" className="email-column">
                  E-post
                </th>
                <th scope="col" className="phone-column">
                  Telefonnummer
                </th>
                <th scope="col" className="menu-column">
                  <span className="visually-hidden">Meny</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredOrganizations.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    Inga organisationer matchar filtret
                  </td>
                </tr>
              ) : (
                filteredOrganizations.map((organization) => (
                  <tr key={organization.id} className="organization-row">
                    <td className="name-column align-middle">{organization.name}</td>
                    <td className="address-column align-middle">
                      {organization.address}, {organization.postalCode} {organization.city}
                    </td>
                    <td className="email-column align-middle">{organization.email}</td>
                    <td className="phone-column align-middle">{organization.phoneNumber}</td>
                    <td className="menu-column p-0">
                      <ListItemDropDownMenu
                        menuItems={[
                          {
                            type: MenuItemType.Delete,
                            label: 'Ta bort',
                            onClick: () => {
                              setModalTriggerElement(organization.id.toString());
                              setSelectedOrganization(organization);
                            },
                          },
                          {
                            type: MenuItemType.Edit,
                            label: 'Redigera',
                            link: `/organisation/${organization.id}`,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {deleteOrganizationStatus.status === Status.failed && (
            <Alert show={true} heading="Kunde inte ta bort organisationen" closable={false} className="mb-4" />
          )}
        </div>
      )}
    </>
  );
}

export default Organizations;